import "core-js/modules/es.date.to-string.js";
import "core-js/modules/es.object.to-string.js";
import "core-js/modules/es.regexp.to-string.js";
import "core-js/modules/es.number.constructor.js";
import "core-js/modules/es.number.is-nan.js";
import "core-js/modules/es.array.includes.js";
import "core-js/modules/es.string.includes.js";
import "core-js/modules/es.string.trim.js";
import "core-js/modules/es.array.some.js";
import "core-js/modules/es.array.iterator.js";
import "core-js/modules/es.set.js";
import "core-js/modules/es.string.iterator.js";
import "core-js/modules/web.dom-collections.iterator.js";
import { z } from 'zod';
var Condition = z.union([z.object({
  component: z.string(),
  operator: z.union([z.literal('isEqual'), z.literal('isNotEqual')]),
  // workaround for weird form.io behavior where it sometimes converts
  // numeric string values to type number when passing conditions
  value: z.string().or(z.number()).transform(function (value) {
    return value.toString();
  })
}), z.object({
  component: z.string(),
  operator: z.union([z.literal('isEmpty'), z.literal('isNotEmpty')])
})]);
var CommonPropertiesSchema = z.object({
  page: z.string().nullish().transform(function (value) {
    return value ? value : '1';
  })
});
var CommonComponentProperties = z.object({
  // TODO: remove this if we verify that key should always be a string - https://applyboard.slack.com/archives/C04PKSZ1F39/p1717126685043449
  // will be fixed in https://applyboard.atlassian.net/browse/AWA-5282
  key: z.union([z.string(), z.number()]).transform(function (value) {
    return value.toString();
  }),
  properties: CommonPropertiesSchema.nullish(),
  conditional: z.object({
    show: z.literal(true),
    conjunction: z.union([z.literal('all'), z.literal('any')]),
    conditions: z.array(Condition)
  }).nullish()
});
var CommonFieldProperties = CommonComponentProperties.extend({
  label: z.string(),
  disabled: z["boolean"]().optional(),
  description: z.string().optional()
});
var CommonHtmlElementProperties = CommonComponentProperties.extend({
  type: z.literal('htmlelement'),
  content: z.string()
});
var FormOption = z.object({
  label: z.string(),
  value: z.string()
});
export var FileInfoSchema = z.object({
  errorMessage: z.string().optional(),
  warningMessage: z.string().optional(),
  uploading: z["boolean"]().optional(),
  file: z["instanceof"](File),
  id: z.string()
});
var CurrencyPropertiesSchema = CommonPropertiesSchema.extend({
  amountCents: z.union([z.string(), z.number()]).nullish().transform(function (value) {
    if (typeof value === 'string' || typeof value === 'number') {
      return Number(value);
    }
    return value;
  }).refine(function (value) {
    return value !== null || value !== undefined ? !Number.isNaN(value) : true;
  }, {
    message: 'amountCents must be a number or numeric string'
  }),
  currency: z["enum"](['USD', 'CAD', 'GBP', 'AUD', 'EUR']).optional()
});
export var TimelessDateString = z.string().regex(/^\d\d\d\d-\d\d-\d\d$/, {
  message: 'Date should be in the form YYYY-MM-DD or an empty string'
}).or(z.literal(''));

/**
 * Custom components that are not built into form.io.  Custom "type" values are
 * allowed by form.io, as explained here:
 * https://help.form.io/developers/form-development/custom-components
 */
var CustomComponents = z.union([CommonComponentProperties.extend({
  type: z.literal('paymentprompt'),
  properties: CurrencyPropertiesSchema.extend({
    paymentLink: z.string(),
    page: z.string().nullish().transform(function (value) {
      return value ? value : '1';
    })
  })
}), CommonComponentProperties.extend({
  type: z.literal('paymentframe'),
  properties: CurrencyPropertiesSchema.extend({
    manageLink: z.string().nullish()
  }).nullish()
})]);
var StaticSelectDefinition = CommonFieldProperties.extend({
  type: z.literal('select'),
  defaultValue: z.string().nullish(),
  validate: z.object({
    required: z["boolean"]().optional()
  }).nullish(),
  data: z.object({
    values: z.array(FormOption)
  }),
  searchEnabled: z["boolean"]()["default"](true),
  properties: z.object({
    hiddenOptions: z.string().refine(function (value) {
      return !value.includes(' ');
    }, {
      message: 'hiddenOptions can not contain spaces'
    }).nullish(),
    page: z.string().nullish()
  }).nullish()
});
var DynamicSelectDefinition = CommonFieldProperties.extend({
  type: z.literal('select'),
  defaultValue: z.string().nullish(),
  validate: z.object({
    required: z["boolean"]().optional()
  }).nullish(),
  dataSrc: z.literal('url'),
  data: z.object({
    url: z.string().describe('The URL to fetch the options from')
  }),
  valueProperty: z.string().describe("The path to the item's value or ID"),
  template: z.string().describe('The mustache template for rendering the display label'),
  selectValues: z.string().nullish().describe('The key in the response that contains the array of options.  If not provided, the response itself is assumed to be the array of options.'),
  disableLimit: z.literal(true),
  searchField: z.string().describe("The query param name to use to pass the user's search term to as they are typing"),
  searchEnabled: z.literal(true)["default"](true).optional(),
  properties: z.object({
    page: z.string().nullish()
  }).nullish()
});
var BooleanOrString = z.union([z.string(), z["boolean"]()]).transform(function (value) {
  return value === true || value === 'true';
});
/**
 * Represents a run-time validation schema for the expected form structure
 */
export var FormDefinition = z.object({
  components: z.array(z.union([CommonFieldProperties.extend({
    type: z.literal('file'),
    defaultValue: z.union([z.string(), FileInfoSchema]).array().nullish(),
    multiple: z["boolean"]().nullish(),
    fileTypes: z.array(z.object({
      label: z.string(),
      value: z.string()
    })).nullish().transform(function (values) {
      return (
        // Form io returns [{ label: '', value: '' }] when no file types are specified
        (values === null || values === void 0 ? void 0 : values.length) === 1 && !values[0].label.trim() && !values[0].value.trim() ? undefined : values
      );
    }),
    fileMaxSize: z.union([z.number(), z.string()]).nullish().transform(function (value) {
      return !isNaN(Number(value)) ? Number(value) : undefined;
    }),
    validate: z.object({
      required: z["boolean"]().optional()
    }).nullish(),
    properties: CurrencyPropertiesSchema.extend({
      fileMaxCount: z.union([z.number(), z.string()]).nullish().transform(function (value) {
        return value === undefined || value === null ? undefined : Number(value);
      }).refine(function (value) {
        return value === undefined || value > 0;
      }, {
        message: 'fileMaxCount must be greater than 0'
      }),
      requiresNotarization: BooleanOrString.nullish(),
      requiresPlagiarismCheck: BooleanOrString.nullish(),
      applyproofCompatible: BooleanOrString.nullish(),
      verifiedByApplyproof: BooleanOrString.nullish(),
      nameCheckRequired: BooleanOrString.nullish(),
      attachmentTypeCheckTag: z.string().nullish()
    }).nullish()
  }), CommonFieldProperties.extend({
    type: z.literal('checkbox'),
    defaultValue: z["boolean"]().nullish(),
    validate: z.object({
      required: z["boolean"]().optional()
    }).nullish()
  }), CommonFieldProperties.extend({
    type: z.literal('day'),
    defaultValue: z.union([z.literal('00/00/0000').transform(function () {
      return undefined;
    }), TimelessDateString]).nullish(),
    validate: z.object({
      required: z["boolean"]().optional()
    }).nullish()
  }), CommonFieldProperties.extend({
    type: z.literal('number'),
    defaultValue: z.string().nullish(),
    validate: z.object({
      required: z["boolean"]().optional(),
      min: z.number().optional(),
      max: z.number().optional()
    }).nullish()
  }), CommonFieldProperties.extend({
    type: z.literal('radio'),
    values: z.array(FormOption),
    defaultValue: z.string().nullish(),
    validate: z.object({
      required: z["boolean"]().optional()
    }).nullish(),
    properties: z.object({
      hiddenOptions: z.string().refine(function (value) {
        return !value.includes(' ');
      }, {
        message: 'hiddenOptions can not contain spaces'
      }).nullish(),
      page: z.string().nullish()
    }).nullish()
  }), StaticSelectDefinition, DynamicSelectDefinition, CommonFieldProperties.extend({
    type: z.literal('selectboxes'),
    defaultValue: z.string().array().nullish(),
    validate: z.object({
      required: z["boolean"]().optional()
    }).nullish(),
    values: z.array(FormOption)
  }), CommonFieldProperties.extend({
    type: z.literal('textarea'),
    defaultValue: z.string().nullish(),
    validate: z.object({
      required: z["boolean"]().optional()
    }).nullish()
  }), CommonFieldProperties.extend({
    type: z.literal('textfield'),
    defaultValue: z.string().nullish(),
    validate: z.object({
      required: z["boolean"]().optional(),
      pattern: z.string().optional()
    }).nullish()
  }), CommonHtmlElementProperties.extend({
    tag: z.literal('a'),
    attrs: z.array(z.union([z.object({
      attr: z.literal('href'),
      value: z.string()
    }), z.object({
      attr: z.literal('target'),
      value: z.literal('_blank')
    })])).refine(oneAttrPerComponent, {
      message: 'Only one value can be provided for each HTML element attribute.'
    })
  }).refine(function (value) {
    var _value$attrs;
    return !!((_value$attrs = value.attrs) !== null && _value$attrs !== void 0 && _value$attrs.some(function (attribute) {
      return attribute.attr === 'href';
    }));
  }, {
    message: "A value for the href attribute must be provided for 'a' tags"
  }), CommonHtmlElementProperties.extend({
    tag: z.literal('p')["default"]('p')
  }), CommonHtmlElementProperties.extend({
    tag: z.literal('div'),
    attrs: z.array(z.object({
      attr: z.literal('data-markdown'),
      value: z.literal('true')
    })).refine(oneAttrPerComponent, {
      message: 'Only one value can be provided for each HTML element attribute.'
    })
  }), CustomComponents]))
  // this check might be dropped if we ever support this scenario
  // see https://applyboard.slack.com/archives/C06ME2XT59V/p1717080783677469
  .refine(checkPaymentTypes, {
    message: 'A formDefinition cannot have components of type paymentprompt and paymentframe simultaneously'
  })
});

/**
 * Represents the type definition for the form definition prior to
 * being parsed and default/transform applied to optional/nullish attributes
 */

/**
 * Represents a compile-time type definition for the expected form structure
 */

function oneAttrPerComponent(value) {
  var attributes = new Set();
  return !value.some(function (attribute) {
    return attributes.size === attributes.add(attribute.attr).size;
  });
}
export function checkPaymentTypes(components) {
  var paymentTypes = components.some(function (item) {
    return item.type === 'paymentprompt';
  }) && components.some(function (item) {
    return item.type === 'paymentframe';
  });
  return !paymentTypes;
}

/**
 * This is a bit of a hack.  Given a component, it will change `props.value` to
 * `unknown` and `props.onChange` to `(newValue: unknown) => void`.  This is
 * required because due to the dynamic nature of `DynamicForm`, all the fields
 * have a value of `unknown`.  You use it like this:
 * ```
 * <Field
 *   as={Combobox as AllowUnknownValue<typeof Combobox>}
 * />
 *
 * It should be safe at runtime, because we validate the initialData matches the
 * formDefinition within `copyFormDataOntoFormDefinition`
 *
 * This is probably a bit better than just using `@ts-expect-error`, because
 * `@ts-expect-error` was hiding other errors for other props on each Field
 *
 * But there's probably a better way to do this that avoids the `as` cast, perhaps by
 * using a wrapper component that validates `value` before passing it to the underlying
 * component
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any